import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { animated } from '@react-spring/web'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Routes, TemplateTarget } from '../../models/enums'
import BackButton from '../Menus/Common/BackButton'
import CloseButton from '../Common/CloseButton'

interface TemplateGlobalProps {
  target?: TemplateTarget
  display?: boolean
  animation?: any
  remote?: boolean
  onClick?: (event?) => void
  children: ReactNode
}

enum ClosingButton {
  none = 1,
  cross,
  text,
  mobileTextBack,
  mobileTextBackConversation
}

const TemplateGlobal = ({ target, display, animation, remote, onClick, children }: TemplateGlobalProps): JSX.Element => {
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const useOldRemote = useSelector((state: RootState) => state.retorikReducer.configuration.useOldRemote)
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)
  const isLandscape = useSelector((state: RootState) => state.viewReducer.isLandscape)
  const isBorne = useSelector((state: RootState) => state.viewReducer.isBorne)
  const displayControls = useSelector((state: RootState) => state.viewReducer.displayControls)
  const route = useSelector((state: RootState) => state.viewReducer.route)
  const [colStart, setColStart] = useState<number>(1)
  const [zIndex, setZIndex] = useState<number>(30)
  const [justifySelf, setJustifySelf] = useState<string>('unset')
  const [background, setBackground] = useState<string>('linear-gradient(to right bottom, #F8F8FC , #E1E2E8)')
  const [marginBottom, setMarginBottom] = useState<string | number>(0)

  useEffect(() => {
    switch (target) {
      case TemplateTarget.languages:
      case TemplateTarget.history:
        setColStart(isLandscape ? 5 : 1)
        setJustifySelf(isLandscape ? 'center' : 'unset')
        setBackground('#FFF')
        setMarginBottom(isMobile && displayControls ? '3rem' : 0)
        break
      case TemplateTarget.map:
        setZIndex(20)
        break
      case TemplateTarget.emergency:
      case TemplateTarget.weather:
        setBackground(isMobile ? '#FFF' : 'linear-gradient(to right bottom, #F2F8F8FC , #F2E1E2E8)')
        break
      case TemplateTarget.mobileConversation:
        setZIndex(3)
        setJustifySelf('center')
        setBackground('#FFFFFFCC')
        break
      case TemplateTarget.mobileOther:
        setBackground('#FFF')
        break
      case TemplateTarget.modal:
        setZIndex(50)
        setBackground('#121212B3')
        break
      default:
        setZIndex(30)
        setBackground('linear-gradient(to right bottom, #F8F8FC , #E1E2E8)')
        break
    }

    setMarginBottom(isMobile && displayControls && target !== TemplateTarget.mobileConversation ? '3rem' : 0)
  }, [target, isLandscape, isMobile])

  const closingButton = useMemo<ClosingButton>(() => {
    if (isMobile) {
      switch (target) {
        case TemplateTarget.languages:
          return ClosingButton.mobileTextBack
        case TemplateTarget.mobileConversation:
        case TemplateTarget.modal:
        case TemplateTarget.list:
        case TemplateTarget.detailedAttachment:
          return ClosingButton.none
        case TemplateTarget.mobileOther:
        case TemplateTarget.weather:
          return ClosingButton.mobileTextBackConversation
        default:
          return ClosingButton.mobileTextBack
      }
    } else if (isLandscape) {
      switch (target) {
        case TemplateTarget.languages:
        case TemplateTarget.history:
          return ClosingButton.text
        case TemplateTarget.list:
        case TemplateTarget.detailedAttachment:
          return ClosingButton.cross
        default:
          // TODO use TemplateGlobal in NewsView and change this condition
          return !useOldRemote && route !== Routes.News ? ClosingButton.cross : ClosingButton.none
      }
    }

    return ClosingButton.none
  }, [target, isMobile, isLandscape, useOldRemote, route, displayControls])

  return (
    <animated.div
      id={`retorik-framework-template-${target}`}
      className='rf-relative rf-max-h-full rf-col-span-full rf-row-start-1 rf-row-span-full rf-text-trueblack rf-flex rf-flex-col rf-items-center'
      style={{
        visibility: display === false ? 'hidden' : 'visible',
        width: isLandscape && (target === TemplateTarget.languages || target === TemplateTarget.history) ? '60%' : target === TemplateTarget.mobileConversation ? '100%' : 'unset',
        maxWidth: target === TemplateTarget.mobileConversation ? '100%' : 'unset',
        marginRight: !isMobile && remote && !(isLandscape && (target === TemplateTarget.languages || target === TemplateTarget.history)) ? '7.5rem' : 0,
        marginBottom: marginBottom,
        zIndex: zIndex,
        gridColumnStart: colStart,
        justifyContent: target === TemplateTarget.modal ? 'center' : undefined,
        justifySelf: justifySelf,
        background: background,
        ...animation
      }}
      onClick={target === TemplateTarget.modal ? (event) => onClick?.(event) : () => {}}
    >
      {/* Back / close button */}
      {closingButton === ClosingButton.none ? (
        <React.Fragment />
      ) : closingButton === ClosingButton.cross ? (
        <CloseButton onClick={() => onClick?.()} showLabel={true} />
      ) : (
        <BackButton title={closingButton === ClosingButton.mobileTextBack ? translation.common.back : translation.menu.backconversation} handleBack={() => onClick?.()} />
      )}

      <div
        className={`rf-h-full rf-flex rf-flex-col ${isBorne ? 'rf-text-size-auto' : ''} rf-scrollbar-hidden`}
        style={{
          width: !isMobile && (target === TemplateTarget.languages || target === TemplateTarget.history) ? 'fit-content' : '100%',
          maxWidth: isBorne && (target === TemplateTarget.languages || target === TemplateTarget.history) ? '66%' : '100%',
          overflow: target === TemplateTarget.mobileConversation ? 'hidden' : 'scroll'
        }}
      >
        {children}
      </div>
    </animated.div>
  )
}

export default TemplateGlobal
